import React, { useCallback, useEffect, useRef, useState } from "react";
import { Radio as RadioIcon, Plus, Trash2, Play, Square, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * RestreamPanel — multi-destination RTMP relay.
 *
 * Takes a MediaStream factory (so we can re-subscribe when Go Live is hit)
 * and pushes MediaRecorder WebM chunks to `/api/ws/radio/restream/{session_id}`
 * which spawns one FFmpeg child-proc per active destination.
 *
 * Permissions: returns an informational "request access" state when the
 * current user lacks `can_restream`.  Admin/premium-creator who have the
 * flag see the full UI.
 *
 * Props:
 *   getBroadcastStream() → MediaStream | null   — required. Must include
 *     both video and audio tracks. For audio-only clients, compose with
 *     a canvas captureStream so we always have a video track.
 *   title?: string — panel label (defaults to "Multistream")
 *   canRestream: boolean  — from upstream user context
 *   backendUrl: string    — REACT_APP_BACKEND_URL
 *   token: string         — JWT (we already store in localStorage.tv_token)
 */
const PLATFORMS = [
  { key: "youtube",  label: "YouTube Live",  hint: "rtmp://a.rtmp.youtube.com/live2", placeholder: "Your YouTube stream key" },
  { key: "facebook", label: "Facebook Live", hint: "rtmps://live-api-s.facebook.com:443/rtmp", placeholder: "Your FB stream key" },
  { key: "twitch",   label: "Twitch",        hint: "rtmp://<region>.contribute.live-video.net/app", placeholder: "Your Twitch stream key" },
  { key: "kick",     label: "Kick",          hint: "rtmps://fa723fc1b171.global-contribute.live-video.net/app", placeholder: "Your Kick stream key" },
  { key: "restream", label: "Restream.io",   hint: "rtmp://live.restream.io/live", placeholder: "Your Restream stream key" },
  { key: "custom",   label: "Custom RTMP",   hint: "rtmp://…", placeholder: "Your stream key" },
];

export default function RestreamPanel({ getBroadcastStream, meta, title = "Multistream to socials" }) {
  const [me, setMe] = useState(null);
  const [dests, setDests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ label: "", platform: "youtube", rtmp_url: "", stream_key: "" });
  const [live, setLive] = useState(false);
  const [error, setError] = useState(null);
  const [activeCount, setActiveCount] = useState(0);
  const wsRef = useRef(null);
  const recRef = useRef(null);

  const loadMe = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setMe(data); } catch {}
  }, []);
  const loadDests = useCallback(async () => {
    try { const { data } = await api.get("/radio/restream/destinations"); setDests(data || []); }
    catch (e) {
      if (e?.response?.status !== 403) setError(formatApiError(e));
      setDests([]);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMe(); loadDests(); }, [loadMe, loadDests]);

  const canRestream = !!(me?.role === "admin" || me?.can_restream);

  const addDest = async () => {
    if (!form.label || !form.rtmp_url || !form.stream_key) {
      toast.error("Fill label + RTMP URL + stream key"); return;
    }
    try {
      await api.post("/radio/restream/destinations", { ...form, enabled: true });
      setForm({ label: "", platform: "youtube", rtmp_url: "", stream_key: "" });
      setAdding(false); loadDests(); toast.success("Destination saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleDest = async (d) => {
    try { await api.patch(`/radio/restream/destinations/${d.id}`, { enabled: !d.enabled }); loadDests(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteDest = async (d) => {
    if (!window.confirm(`Delete "${d.label}"?`)) return;
    try { await api.delete(`/radio/restream/destinations/${d.id}`); loadDests(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const goLive = async () => {
    const enabled = dests.filter((d) => d.enabled);
    if (enabled.length === 0) { toast.error("Enable at least one destination"); return; }
    const stream = await Promise.resolve(getBroadcastStream && getBroadcastStream());
    if (!stream || !(stream instanceof MediaStream)) {
      toast.error("No broadcast stream available — start the studio first"); return;
    }
    const hasVideo = stream.getVideoTracks().length > 0;
    if (!hasVideo) {
      toast.error("Platforms like YouTube/Facebook require video. Enable the canvas-video in settings first.");
      return;
    }
    const sessionId = `rs_${Math.random().toString(36).slice(2, 10)}`;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const tok = localStorage.getItem("tv_token") || "";
    const ws = new WebSocket(`${base}/api/radio/restream/ws/${sessionId}?token=${encodeURIComponent(tok)}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "start", destination_ids: enabled.map((d) => d.id) }));
    };
    ws.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data);
        if (m.type === "started") {
          setLive(true); setActiveCount(m.count || 0);
          toast.success(`LIVE on ${m.count} destination${m.count > 1 ? "s" : ""}`);
          const mime = ["video/webm;codecs=vp8,opus", "video/webm;codecs=vp9,opus", "video/webm"]
            .find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "video/webm";
          const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2_500_000, audioBitsPerSecond: 128_000 });
          rec.ondataavailable = (e) => {
            if (e.data && e.data.size && ws.readyState === 1) {
              e.data.arrayBuffer().then((buf) => { try { ws.send(buf); } catch {} });
            }
          };
          rec.onstop = () => { try { ws.send(JSON.stringify({ type: "stop" })); } catch {}; try { ws.close(); } catch {} };
          recRef.current = rec;
          rec.start(1000);
        } else if (m.type === "error") {
          toast.error(`Multistream: ${m.error}`); stopLive();
        }
      } catch {}
    };
    ws.onerror = () => { setError("WebSocket error"); };
    ws.onclose = () => {
      if (recRef.current && recRef.current.state !== "inactive") { try { recRef.current.stop(); } catch {} }
      recRef.current = null; wsRef.current = null;
      setLive(false); setActiveCount(0);
    };
  };

  const stopLive = () => {
    try { recRef.current?.stop?.(); } catch {}
    try { wsRef.current?.close?.(); } catch {}
    setLive(false);
  };

  useEffect(() => () => { stopLive(); }, []);

  if (loading) {
    return <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-white/50 text-xs">Loading multistream…</div>;
  }

  if (!canRestream) {
    return (
      <div data-testid="restream-gated" className="rounded-lg border border-white/10 bg-black/30 p-3">
        <div className="flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 text-amber-400" />
          <div className="text-white font-black text-[11px] uppercase tracking-wider">Multistream — access required</div>
        </div>
        <div className="text-white/60 text-[10px] mt-1 leading-snug">
          Pushing your show to YouTube / Facebook / Twitch / Kick / Restream requires admin approval. Ask your admin to grant the <code className="text-tv-accent">restream</code> permission on your account in the Admin → Permissions tab.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="restream-panel" className="rounded-lg border border-white/10 bg-black/30">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <RadioIcon className={`w-3.5 h-3.5 ${live ? "text-red-400 animate-pulse" : "text-fuchsia-300"}`} />
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-[11px] uppercase tracking-wider">{title}</div>
          <div className="text-white/50 text-[10px] truncate">
            {live ? `LIVE on ${activeCount} destination${activeCount > 1 ? "s" : ""}` : `${dests.filter(d => d.enabled).length} of ${dests.length} destinations enabled`}
          </div>
        </div>
        {live && <span className="inline-flex items-center gap-1 bg-tv-live/20 text-red-200 border border-tv-live/40 text-[9px] font-black px-2 py-0.5 rounded-full">LIVE</span>}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          {/* Go live button */}
          {!live ? (
            <button
              data-testid="restream-golive"
              onClick={goLive}
              disabled={dests.filter(d => d.enabled).length === 0}
              className="w-full inline-flex items-center justify-center gap-1.5 bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-40 text-black font-black text-[11px] py-2 rounded"
            >
              <Play className="w-3 h-3" /> GO LIVE EVERYWHERE
            </button>
          ) : (
            <button
              data-testid="restream-stop"
              onClick={stopLive}
              className="w-full inline-flex items-center justify-center gap-1.5 bg-red-500 hover:bg-red-400 text-white font-black text-[11px] py-2 rounded animate-pulse"
            >
              <Square className="w-3 h-3" /> END MULTISTREAM
            </button>
          )}

          {/* Destinations list */}
          <div className="space-y-1" data-testid="restream-destinations">
            {dests.length === 0 && (
              <div className="text-white/40 text-[10px] text-center py-2">
                No destinations yet. Add one below.
              </div>
            )}
            {dests.map((d) => (
              <div key={d.id} data-testid={`restream-dest-${d.id}`} className="bg-white/5 rounded p-1.5 flex items-center gap-2">
                <input
                  type="checkbox" checked={!!d.enabled} onChange={() => toggleDest(d)}
                  data-testid={`restream-dest-toggle-${d.id}`}
                  className="accent-fuchsia-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-[10px] font-bold truncate">{d.label}</div>
                  <div className="text-white/50 text-[9px] truncate">{d.platform} · {d.rtmp_url}</div>
                </div>
                <button onClick={() => deleteDest(d)} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 p-1 rounded">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          {/* Add form */}
          {adding ? (
            <div className="bg-black/40 border border-white/10 rounded p-2 space-y-1.5" data-testid="restream-add-form">
              <div className="grid grid-cols-2 gap-1.5">
                <input
                  data-testid="restream-add-label"
                  placeholder="Label (e.g. 'My YouTube')"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  className="bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[10px]"
                />
                <select
                  data-testid="restream-add-platform"
                  value={form.platform}
                  onChange={(e) => {
                    const p = PLATFORMS.find(x => x.key === e.target.value);
                    setForm({ ...form, platform: e.target.value, rtmp_url: p?.hint || form.rtmp_url });
                  }}
                  className="bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[10px]"
                >
                  {PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <input
                data-testid="restream-add-url"
                placeholder="RTMP URL (rtmp://…)"
                value={form.rtmp_url}
                onChange={(e) => setForm({ ...form, rtmp_url: e.target.value })}
                className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[10px] font-mono"
              />
              <input
                data-testid="restream-add-key"
                placeholder={PLATFORMS.find(p => p.key === form.platform)?.placeholder || "Stream key"}
                value={form.stream_key}
                onChange={(e) => setForm({ ...form, stream_key: e.target.value })}
                className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[10px] font-mono"
              />
              <div className="flex gap-1.5">
                <button
                  data-testid="restream-add-save"
                  onClick={addDest}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-[10px] py-1.5 rounded"
                >Save</button>
                <button
                  onClick={() => setAdding(false)}
                  className="bg-white/5 hover:bg-white/15 text-white font-black text-[10px] py-1.5 px-3 rounded"
                >Cancel</button>
              </div>
            </div>
          ) : (
            <button
              data-testid="restream-add-open"
              onClick={() => setAdding(true)}
              className="w-full inline-flex items-center justify-center gap-1 bg-white/5 hover:bg-white/15 text-white/80 font-black text-[10px] py-1.5 rounded border border-dashed border-white/20"
            >
              <Plus className="w-3 h-3" /> Add destination
            </button>
          )}

          <div className="text-white/40 text-[9px] italic">
            Tip: YouTube & Facebook need ~30s after you hit Go Live before their "Start streaming" button un-greys. Twitch / Kick / Restream start accepting within seconds.
          </div>
          {error && <div className="text-red-300 text-[9px]">{error}</div>}
        </div>
      )}
    </div>
  );
}
